import { useState } from 'react';
import type { BookmarkNode } from '../types';
import { uid } from '../lib/ids';
import { faviconFor } from '../lib/urls';

interface Props {
  root: BookmarkNode;
  onChange: (root: BookmarkNode) => void;
  onOpen: (url: string) => void;
}

function clone<T>(value: T): T { return JSON.parse(JSON.stringify(value)); }

function updateNode(root: BookmarkNode, id: string, updater: (node: BookmarkNode) => BookmarkNode): BookmarkNode {
  if (root.id === id) return updater(root);
  return { ...root, children: root.children?.map((child) => updateNode(child, id, updater)) };
}

function removeNode(root: BookmarkNode, id: string): BookmarkNode {
  return { ...root, children: root.children?.filter((child) => child.id !== id).map((child) => removeNode(child, id)) };
}

function addChild(root: BookmarkNode, parentId: string, child: BookmarkNode): BookmarkNode {
  return updateNode(root, parentId, (node) => ({ ...node, children: [...(node.children || []), child], updatedAt: Date.now() }));
}

function findNode(root: BookmarkNode, id: string): BookmarkNode | undefined {
  if (root.id === id) return root;
  for (const child of root.children || []) {
    const found = findNode(child, id);
    if (found) return found;
  }
  return undefined;
}

export function BookmarkManager({ root, onChange, onOpen }: Props) {
  const [selectedFolder, setSelectedFolder] = useState('root');
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const folder = findNode(root, selectedFolder) || root;

  function addBookmark() {
    if (!title || !url) return;
    const now = Date.now();
    onChange(addChild(root, selectedFolder, { id: uid('bm'), type: 'bookmark', title, url, favicon: faviconFor(url), createdAt: now, updatedAt: now }));
    setTitle(''); setUrl('');
  }

  function addFolder() {
    const name = title || 'New Folder';
    const now = Date.now();
    onChange(addChild(root, selectedFolder, { id: uid('folder'), type: 'folder', title: name, children: [], createdAt: now, updatedAt: now }));
    setTitle('');
  }

  function rename(id: string) {
    const next = prompt('New title');
    if (!next) return;
    onChange(updateNode(root, id, (node) => ({ ...node, title: next, updatedAt: Date.now() })));
  }

  function onDrop(targetFolderId: string, draggedId: string) {
    const dragged = findNode(root, draggedId);
    if (!dragged || dragged.id === targetFolderId) return;
    const without = removeNode(clone(root), draggedId);
    onChange(addChild(without, targetFolderId, dragged));
  }

  function renderTree(node: BookmarkNode) {
    return (
      <div key={node.id} className={`bookmark-node ${node.type}`} draggable={node.id !== 'root'} onDragStart={(event) => event.dataTransfer.setData('text/plain', node.id)}>
        <div className="bookmark-node-row" onDragOver={(event) => node.type === 'folder' && event.preventDefault()} onDrop={(event) => onDrop(node.id, event.dataTransfer.getData('text/plain'))}>
          {node.type === 'folder' ? <button onClick={() => setSelectedFolder(node.id)}>📁 {node.title}</button> : <button onClick={() => node.url && onOpen(node.url)}>🔖 {node.title}</button>}
          {node.id !== 'root' && <button onClick={() => rename(node.id)}>Edit</button>}
          {node.id !== 'root' && <button onClick={() => onChange(removeNode(root, node.id))}>Delete</button>}
        </div>
        {node.type === 'folder' && <div className="bookmark-children">{(node.children || []).map(renderTree)}</div>}
      </div>
    );
  }

  return (
    <section className="internal-page bookmark-manager">
      <header className="page-header"><div><p className="eyebrow">websurf://bookmarks</p><h1>Bookmarks</h1></div><span>Drag bookmarks into folders to reorganize.</span></header>
      <div className="manager-grid">
        <div className="list-card tree-card">{renderTree(root)}</div>
        <aside className="editor-card">
          <h2>Add to {folder.title}</h2>
          <input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Title or folder name" />
          <input value={url} onChange={(event) => setUrl(event.target.value)} placeholder="https://example.com" />
          <div className="button-row"><button onClick={addBookmark}>Add bookmark</button><button onClick={addFolder}>Add folder</button></div>
        </aside>
      </div>
    </section>
  );
}
